import { useCallback } from 'react';
import { SprintAPI } from '../utils/api';
import {
  createTemplateFromCampaign,
  loadTemplateTreeForCopy,
  persistTemplateTree,
} from '../utils/templateCopy';

/**
 * useTemplateActions Hook
 *
 * Templates are campaigns with `isTemplate: true`. Rename / icon / update reuse
 * the sprint handlers. This hook only owns template-specific create + duplicate.
 */
export function useTemplateActions({
  sprints,
  setSprints,
  loadFullSprint,
  handleSelectTemplate,
  handleUpdateSprint,
  handleRenameSprint,
  handleSprintIconChange,
}) {
  // Templates edit through the same path as campaigns
  const handleUpdateTemplate = handleUpdateSprint;
  const handleRenameTemplate = handleRenameSprint;
  const handleTemplateIconChange = handleSprintIconChange;

  // Duplicate a template (full tree, proper ID remapping)
  const handleDuplicateTemplate = useCallback(async (template) => {
    if (!template?.id) return;

    let fullRoot;
    let loadedSprints;
    try {
      const loaded = await loadTemplateTreeForCopy(template.id, sprints, loadFullSprint);
      fullRoot = loaded.root;
      loadedSprints = loaded.sprints;
    } catch (error) {
      console.error('[useTemplateActions] Failed to load template for duplicate:', error);
      return;
    }

    if (!fullRoot) {
      console.error('[useTemplateActions] Template not found for duplicate:', template.id);
      return;
    }

    const { rootTemplate, nestedCampaigns } = createTemplateFromCampaign(
      fullRoot,
      loadedSprints,
      { newName: `${fullRoot.name} (Copy)` },
    );

    try {
      const { createdTemplate, createdNested } = await persistTemplateTree({
        rootTemplate,
        nestedCampaigns,
        SprintAPI,
      });

      setSprints(prev => [...prev, createdTemplate, ...createdNested]);
      handleSelectTemplate(createdTemplate);
      console.log(
        '✅ Template duplicated:',
        createdTemplate.name,
        'with',
        createdNested.length,
        'nested campaigns',
      );
    } catch (error) {
      console.error('[useTemplateActions] Failed to duplicate template:', error);
    }
  }, [sprints, loadFullSprint, setSprints, handleSelectTemplate]);

  // Create a blank template
  const handleTemplateCreated = useCallback(async (newTemplate, onSuccess) => {
    try {
      const savedTemplate = await SprintAPI.create(newTemplate);
      setSprints(prev => [...prev, savedTemplate]);
      onSuccess?.(savedTemplate);
      return savedTemplate;
    } catch (error) {
      console.error('[useTemplateActions] Failed to create template:', error);
      const tempTemplate = { ...newTemplate, id: `template-${Date.now()}` };
      setSprints(prev => [...prev, tempTemplate]);
      onSuccess?.(tempTemplate);
      return tempTemplate;
    }
  }, [setSprints]);

  return {
    handleUpdateTemplate,
    handleRenameTemplate,
    handleDuplicateTemplate,
    handleTemplateCreated,
    handleTemplateIconChange,
  };
}
